import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from 'modules/styled/web';
import { cashOutCall } from 'modules/wallet/wallet.actions';
import { updateCashoutLog } from 'modules/wallet/earnings.actions';
import { CASHOUT_MAX } from '@r3l/common';
import Tooltip from 'modules/tooltip/tooltip.component';
import { usePrice } from 'modules/wallet/price.context';
import { CashoutFooter } from './cashoutFooter';
import { Row, View, SecondaryText, BodyText, Button, SmallText } from 'modules/styled';
import { useTx, useRel, useWeb3React } from 'web3/hooks';
import { TX_STATUS } from 'web3/utils';
import {
  VerifyEmail,
  renderWarnings,
  Progress,
  ProgressType,
  useVerifyEmail,
  ClaimProps,
  useUnclaimedSig,
  useUnclaimedVest,
} from './claimUtils';
import { showModal } from 'modules/navigation/navigation.actions';

export const CashoutHandler = ({ canClaim, close }: ClaimProps): JSX.Element => {
  const [token, setToken] = useState('');
  const user = useSelector((state: any) => state.auth.user);
  const { account } = useWeb3React();
  const unclaimedSig = useUnclaimedSig(user, account as string | undefined);
  const unclaimedVest = useUnclaimedVest(account as string | undefined);

  const unclaimedUnvestedTokens =
    (unclaimedVest?.shortVest || 0) + (unclaimedVest?.longVest || 0) ||
    user.unvestedTokens;

  const { sendTx, txStatus } = useTx();
  const rel = useRel();
  const maxClaim = Math.min(
    canClaim,
    Math.max(CASHOUT_MAX - user.cashedOut + user.pendingCashout, 0)
  );

  const pendingTx = txStatus === TX_STATUS.PENDING;
  const initAmnt = maxClaim;

  const [amount, setAmount] = useState(initAmnt);
  const dispatch = useDispatch();

  const maxUSD = usePrice(maxClaim);

  const { verifyEmail, sendEmail, sendingEmail, emailError } = useVerifyEmail();

  const startClaim = async () => {
    if (user.email && user.confirmed && !token) return verifyEmail();
    return cashOut();
  };

  const cashOut = async () => {
    const res: any = await dispatch(cashOutCall(amount, token));
    if (!res) return;
    const { amnt, sig } = res;
    if (!rel) {
      // eslint-disable-next-line no-console
      console.error('Missing contract');
      return;
    }
    sendTx(rel, 'claimTokens', [amnt, sig]);
  };

  useEffect(() => {
    if (txStatus === TX_STATUS.CONFIRMED) {
      dispatch(updateCashoutLog(user?.cashOut?.earning));
      close();
    }
  }, [txStatus, dispatch]);

  const minAmnt = unclaimedSig?.amount || 0;
  const validateAmount = (value) => (value < 0 ? 0 : value > maxClaim ? maxClaim : value);
  // const showUnclamedWarning = unclaimedSig && !pendingTx;

  if (sendEmail && !txStatus)
    return (
      <VerifyEmail
        email={user.email}
        cashOut={cashOut}
        setToken={setToken}
        token={token}
      />
    );

  return (
    <Fragment>
      {renderWarnings(false, emailError)}
      <Row>
        <View sx={{ flex: 1, mr: 1 }}>
          <Input
            sx={{
              height: 5,
              mt: 0,
              flex: 1,
              fontSize: amount ? 5 : 4,
              px: 1,
              py: 0,
              fontWeight: 'bold',
            }}
            type="number"
            onChange={({ target: { value } }) => setAmount(validateAmount(value))}
            value={amount}
          />
        </View>

        <Button
          disabled={!amount || amount < minAmnt || amount === 0}
          sx={{
            height: 8,
            mr: 'auto',
          }}
          onPress={startClaim}
        >
          Transfer coins to wallet
        </Button>
      </Row>
      {amount < minAmnt || amount == 0 ? (
        <SmallText sx={{ color: 'red' }}>amount must be greater {minAmnt} REL</SmallText>
      ) : null}{' '}
      <Row>
        <View>
          <SecondaryText sx={{ mt: 1, mr: 2 }} onPress={() => setAmount(maxClaim)}>
            {/* @ts-ignore */}
            <Tooltip
              data={{
                text: 'This is the maximum amount of coins you can claim at this time.',
              }}
            />
            Maximum claim: {maxClaim}
            {maxUSD} Coins
          </SecondaryText>
        </View>
        <View>
          <SecondaryText sx={{ mt: 1 }}>
            {/* @ts-ignore */}
            <Tooltip data={{ text: 'Total unclaimed coins.' }} />
            Unclaimed: {canClaim} Coins
          </SecondaryText>
        </View>
      </Row>
      {unclaimedUnvestedTokens ? (
        <Row sx={{ mt: 4 }}>
          <BodyText>You have {unclaimedUnvestedTokens} unvested tokens. </BodyText>
          <BodyText onPress={() => dispatch(showModal('vest'))} sx={{ color: 'blue' }}>
            Claim Unvested Tokens
          </BodyText>
        </Row>
      ) : null}
      <CashoutFooter />
      {pendingTx || sendingEmail ? (
        <Progress
          type={sendingEmail && !pendingTx ? ProgressType.EMAIL : ProgressType.TX}
        />
      ) : null}
    </Fragment>
  );
};
